import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { SessionEndReason, StorageKey } from '@core/enums';
import { SessionService } from '@core/services';
import { getLocalStorageValue } from '@core/utils';

/**
 * Protects every route that requires an authenticated session.
 *
 * Place on a parent route so that all of its child routes are covered with a single declaration —
 * no per-route duplication needed. A functional guard (review MR !1 N21, the partner half).
 *
 * When credentials are missing the guard delegates to SessionService which
 * already owns the "navigate to welcome" logic via LocalSessionInvalid.
 * Nothing new is needed here: guard → SessionService → sign-in.
 */
export const authGuard: CanActivateFn = (): boolean => {
    const hasUser = !!getLocalStorageValue(StorageKey.UserId);
    const hasToken = !!getLocalStorageValue(StorageKey.AuthToken);

    if (hasUser && hasToken) {
        return true;
    }

    // Session data is absent (never logged in, or storage was cleared).
    // LocalSessionInvalid policy: revokeOnServer=false, navigateToWelcome=true, notice=null.
    // This reuses the identical path that ApiErrorInterceptor uses for server-confirmed
    // session ends — no new redirect logic is introduced here.
    inject(SessionService).endSession(SessionEndReason.LocalSessionInvalid);
    return false;
};
